import { toast } from "react-toastify";
import Header from "./Header";
import Banner from "./Banner";
import InformationLine from "./InformationLine";
import VirtualTourInfoTitle from "./VirtualTourInfoTitle";
import OnlineSchedulingTitle from "./OnlineSchedulingTitle";
import BenefitsSection from "./BenefitsSection";
import Benefit from "./Benefit";
import ServicesSection from "./ServicesSection";
import Service from "./Service";
import PartnersSection from "./PartnersSection";
import Partner from "./Partner";
import ContactSection from "./ContactSection";
import Footer from "./Footer";
import createForm from "~/shared/Form";
import TextInput from "~/shared/TextInput";
import TextareaInput from "~/shared/TextareaInput";
import Button from "./Button";
import yup from "~/utils/yup";
import api from "~/adapters/api";

import matterport1 from "../../../public/images/matterport-1.jpg";
import matterport2 from "../../../public/images/matterport-2.jpg";
import matterport3 from "../../../public/images/matterport-3.jpg";

import matterportScheduling1 from "../../../public/images/matterport-scheduling-1.png";
import matterportScheduling2 from "../../../public/images/matterport-scheduling-2.png";

import matterportBenefits1 from "../../../public/images/matterport-benefits-1.png";
import matterportBenefits2 from "../../../public/images/matterport-benefits-2.png";
import matterportBenefits3 from "../../../public/images/matterport-benefits-3.png";

import matterportServices1 from "../../../public/images/matterport-services-1.jpg";
import matterportServices2 from "../../../public/images/matterport-services-2.jpg";
import matterportServices3 from "../../../public/images/matterport-services-3.jpg";

import logoCasaMineira from "../../../public/images/logo-casa-mineira.png";
import logoYuca from "../../../public/images/logo-yuca.png";
import logoLoft from "../../../public/images/logo-loft.png";
import logoEmCasa from "../../../public/images/logo-emcasa.png";
import logoHousi from "../../../public/images/logo-housi.png";
import logoQuintoAndar from "../../../public/images/logo-quintoandar.png";

const schema = yup.object().shape({
    name: yup.string().required(),
    email: yup
        .string()
        .email()
        .required(),
    phone: yup.string().required(),
    message: yup
        .string()
        .notRequired()
        .nullable()
});

const [Field, Form] = createForm(
    {
        name: {
            label: "Nome",
            component: TextInput,
            placeholder: "Digite seu nome"
        },
        email: {
            label: "E-mail",
            component: TextInput,
            placeholder: "Digite seu e-mail"
        },
        phone: {
            label: "Celular",
            component: TextInput,
            placeholder: "Digite seu celular"
        },
        message: {
            label: "Mensagem",
            component: TextareaInput,
            placeholder: "Mensagem",
            rows: 7
        }
    },
    { validationSchema: schema }
);

const MatterportLandingPage = () => {
    const submit = async (data: Record<string, any>) => {
        const requestData = {
            name: data.name,
            email: data.email,
            phone: data.phone,
            message: data.message,
            product: "matterport"
        };

        return api.lp.sendContact(requestData)
            .then(() => {
                toast.success("Contato enviado com sucesso");
            });
    };

    return (
        <main>
            <Header />

            <Banner>
                Tour virtual 3D<br />
                <span className="featured">Matterport</span>
            </Banner>

            <div style={{ padding: "50px 0" }}>
                <InformationLine
                    id="quem-somos"
                    header="Quem somos"
                    mediaUrl={matterport1}
                    mediaStyle={{ borderTopLeftRadius: "30px", overflow: "hidden" }}
                >
                    <p>Somos uma startup com mais de 15 mil fotógrafos cadastrados no Brasil.</p>

                    <p>Resolvemos a sua demanda por fotos e tour virtual para que você fique focado no seu negócio.</p>
                </InformationLine>

                <InformationLine orientation="right" header="Como resolvemos" mediaUrl={matterport2}>
                    <p>Oferecemos o agendamento das sessões com agilidade e entrega das fotos e tour virtual do seu imóvel em até 1 dia útil após a sessão.</p>

                    <p>Permitimos escalabilidade para o seu negócio, agende uma ou dezenas de sessões no mesmo dia e horário conforme sua necessidade.</p>

                    <p>Ganhamos por serviço, você só paga quando utilizar mantendo o custo variável.</p>
                </InformationLine>

                <InformationLine
                    centerMedia
                    header={<VirtualTourInfoTitle />}
                    media={() => (
                        <>
                            <iframe
                                src="https://my.matterport.com/show/?m=HjDfC32LEg4"
                                frameBorder="0"
                                allowFullScreen
                                allow="xr-spatial-tracking"
                                style={{ width: "100%", height: "100%", minHeight: "330px", borderRadius: "30px" }}
                            />
                        </>
                    )}
                >
                    <ul>
                        <li>Tour virtual 3D com a tecnologia Matterport que oferece qualidade extrema.</li>

                        <li>Permite ao usuário visualizar todo o imóvel, tirar medidas com 99% de precisão além de diversos outros recursos.</li>
                    </ul>
                </InformationLine>
            </div>

            <BenefitsSection title="Vantagens do Tour Virtual">
                <Benefit iconUrl={matterportBenefits1}>
                    <p>Permite que seu cliente visite o imóvel a qualquer dia e horário, e no momento que é bom para ele, mesmo se estiver em outra cidade.</p>
                </Benefit>

                <Benefit iconUrl={matterportBenefits2}>
                    <p>Otimiza o tempo do seu cliente e do corretor, ganhando qualidade nas visitas e contatos. O corretor só vai se deslocar até o imóvel para atender clientes que tem chances reais de fechar negócio.</p>
                </Benefit>

                <Benefit iconUrl={matterportBenefits3}>
                    <p>Possibilita visita virtual guiada, o seu corretor ao mesmo tempo mostrando o imóvel do cliente. Uma experiência inovadora e um diferencial para sua imobiliária.</p>
                </Benefit>
            </BenefitsSection>

            <ServicesSection title="Serviços & Recursos">
                <Service title="Fotos" iconUrl={matterportServices1}>
                    <p>Fotos em alta resolução de todos os ambientes do imóvel, áreas comuns e fachada, prontas para divulgação em portais imobiliários.</p>
                </Service>

                <Service title="Planta baixa" iconUrl={matterportServices2}>
                    <p>Produzimos a planta baixa do imóvel com 99% de precisão e entregamos ela em PDF, JPG e até em DWG para importação em programas CAD.</p>
                </Service>

                <Service title="Vídeo" iconUrl={matterportServices3}>
                    <p>Vídeo de 2 a 3 minutos com o tour virtual, ideal para redes sociais e Youtube contribuindo no aumento de relevância em mecanismos de busca além de ser um formato atraente para vários clientes.</p>
                </Service>
            </ServicesSection>

            <div style={{ padding: "50px 0" }}>
                <InformationLine
                    header={<OnlineSchedulingTitle />}
                    mediaUrl={matterportScheduling1}
                    mediaStyle={{ padding: "30px 60px" }}
                >
                    <p>Facilidade para seus corretores e/ou para o proprietário agendar o job no dia e horário que desejar:</p>

                    <ul>
                        <li>Permite incluir o logo da sua imobiliária.</li>
                        <li>O corretor pode marcar o agendamento por completo ou o proprietário pode receber o link para agendar na data e hora com disponibilidade.</li>
                        <li>Permite receber o código do imóvel do seu sistema para acompanhamento e interação posterior.</li>
                    </ul>
                </InformationLine>

                <InformationLine
                    centerMedia
                    orientation="right"
                    header="Acompanhamento online"
                    mediaUrl={matterportScheduling2}
                    mediaStyle={{ textAlign: "center" }}
                >
                    <p>Relatórios que permitem acompanhar a sua operação em detalhes visualizando os jobs realizados, jobs cancelados, slots disponíveis, taxa de ocupação, SLA de entrega entre diversos outros.</p>
                </InformationLine>
            </div>

            <PartnersSection title="Parceiros do Fotop Serviços">
                <Partner title="Casa Mineira" logo={logoCasaMineira} />
                <Partner title="Yuca" logo={logoYuca} />
                <Partner title="Loft" logo={logoLoft} />
                <Partner title="EmCasa" logo={logoEmCasa} />
                <Partner title="Housi" logo={logoHousi} />
                <Partner title="Quinto Andar" logo={logoQuintoAndar} />
            </PartnersSection>

            <ContactSection title="Solicite mais informações, envie seus dados e entraremos em contato.">
                <h2 id="orcamento">Solicite um orçamento.</h2>

                <Form
                    onSubmit={submit}
                    renderButton={({ loading }) => (
                        <Button type="submit">{ loading ? "Enviando" : "Enviar"}</Button>
                    )}
                >
                    <Field name="name" />
                    <Field name="email" />
                    <Field name="phone" />
                    <Field name="message" />
                </Form>
            </ContactSection>

            <Footer />
        </main>
    );
};

export default MatterportLandingPage;
